import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Image, User, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const GalleryPage = () => {
  const { teacherId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, [teacherId]);

  const fetchGallery = async () => {
    try {
      const response = await api.get(`/gallery/teacher/${teacherId}`);
      setGallery(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const navigateImage = (direction) => {
    if (!gallery?.images) return;
    const newIndex = selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < gallery.images.length) {
      setSelectedImageIndex(newIndex);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowLeft') navigateImage(-1);
      else if (e.key === 'ArrowRight') navigateImage(1);
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, selectedImageIndex]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0EFEA' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0EFEA' }}>
        <Card className="p-8 text-center">
          <p style={{ color: '#5C635A' }}>Gallery not found</p>
          <Link to="/">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EFEA' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 p-4" style={{ backgroundColor: '#163329' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </Link>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Spectral, serif' }}>
            Teacher Gallery
          </h1>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Teacher Info */}
        <Card className="mb-8" style={{ borderColor: '#E3E1DB' }}>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#E3E1DB' }}>
              {gallery.teacher_avatar ? (
                <img src={gallery.teacher_avatar} alt={gallery.teacher_name} className="w-full h-full object-cover" />
              ) : (
                <User className="h-8 w-8" style={{ color: '#5C635A' }} />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }} data-testid="gallery-heading">
                <span className="whitespace-nowrap">{gallery.teacher_name}&apos;s</span> Gallery
              </h2>
              <p className="text-sm" style={{ color: '#5C635A' }}>
                {gallery.total_count} image{gallery.total_count !== 1 ? 's' : ''}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Gallery Empty State */}
        {gallery.is_empty && (
          <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
            <Image className="h-16 w-16 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
            <p style={{ color: '#5C635A' }}>No images yet</p>
          </Card>
        )}

        {/* Image Grid */}
        {gallery.images && gallery.images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <AnimatePresence>
              {gallery.images.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="aspect-square cursor-pointer group relative rounded-lg overflow-hidden"
                  style={{ backgroundColor: '#E3E1DB' }}
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={image.presigned_url || image.url}
                    alt={image.title || `Gallery image ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Image className="h-8 w-8 text-white" />
                  </div>
                  {/* Title Overlay */}
                  {image.title && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-xs truncate">{image.title}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        {gallery.images?.length > 0 && gallery.caption && (
          <p className="text-xs italic mt-4" style={{ color: '#5C635A' }} data-testid="gallery-caption">
            {gallery.caption}
          </p>
        )}
      </div>

      {/* Lightbox Modal */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black/95 border-none"
          style={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="close-lightbox"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            {/* Navigation Buttons */}
            {selectedImageIndex !== null && selectedImageIndex > 0 && (
              <button
                onClick={() => navigateImage(-1)}
                className="absolute left-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="prev-image"
              >
                <ChevronLeft className="h-8 w-8 text-white" />
              </button>
            )}
            {selectedImageIndex !== null && gallery?.images && selectedImageIndex < gallery.images.length - 1 && (
              <button
                onClick={() => navigateImage(1)}
                className="absolute right-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="next-image"
              >
                <ChevronRight className="h-8 w-8 text-white" />
              </button>
            )}

            {/* Current Image */}
            {selectedImageIndex !== null && gallery?.images?.[selectedImageIndex] && (
              <div className="flex flex-col items-center max-h-[90vh] p-4">
                <img
                  src={gallery.images[selectedImageIndex].presigned_url || gallery.images[selectedImageIndex].url}
                  alt={gallery.images[selectedImageIndex].title || 'Gallery image'}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
                {/* Image Info */}
                <div className="mt-4 text-center">
                  {gallery.images[selectedImageIndex].title && (
                    <h3 className="text-white text-lg font-medium">{gallery.images[selectedImageIndex].title}</h3>
                  )}
                  {gallery.images[selectedImageIndex].description && (
                    <p className="text-white/70 text-sm mt-1">{gallery.images[selectedImageIndex].description}</p>
                  )}
                  <p className="text-white/50 text-xs mt-2">
                    {selectedImageIndex + 1} / {gallery.images.length}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
